<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-title>
              <AppPageHeader
                icon="mdi-handshake-outline"
                title="Stock Payments"
                subtitle="Loan balance and installment history for every stock assignment"
                style="width: 100%"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      fab
                      x-small
                      color="primary"
                      to="/assigned_stocks/create"
                    >
                      <v-icon dark> mdi-plus </v-icon>
                    </v-btn>
                  </template>
                  <span>Assign Stock</span>
                </v-tooltip>
              </AppPageHeader>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="4">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search Assignment # or Assignee... "
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.assigned_to_id"
                    :items="users"
                    item-text="display_name"
                    item-value="id"
                    label="Assigned To"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-account"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.payment_status"
                    :items="statusOptions"
                    label="Payment Status"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-cash-check"
                    clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table
                v-else
                fixed-header
                height="500"
                class="striped-table"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr No</th>
                      <th class="text-left">Assignment No.</th>
                      <th class="text-left">Date</th>
                      <th class="text-left">Assigned To</th>
                      <th class="text-right">Total</th>
                      <th class="text-right">Paid</th>
                      <th class="text-right">Pending</th>
                      <th class="text-left">Status</th>
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="assignments.length != 0">
                    <tr v-for="(assignment, i) in assignments" :key="`assignment_${i}`">
                      <td style="min-width: 85px">{{ i + 1 }}</td>
                      <td>{{ assignment.assignment_number }}</td>
                      <td>{{ assignment.assignment_date | niceDate }}</td>
                      <td>
                        {{
                          assignment.assigned_to
                            ? `${assignment.assigned_to.first_name} ${assignment.assigned_to.last_name || ""}`
                            : ""
                        }}
                      </td>
                      <td class="text-right mono-figure">{{ assignment.total_amount }}</td>
                      <td class="text-right mono-figure">{{ assignment.paid_amount }}</td>
                      <td class="text-right mono-figure">{{ assignment.pending_amount }}</td>
                      <td>
                        <v-chip
                          small
                          :color="statusColor(assignment.payment_status)"
                          text-color="white"
                        >
                          {{ statusLabels[assignment.payment_status] }}
                        </v-chip>
                      </td>
                      <td class="text-right">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              class="my-auto"
                              color="primary"
                              @click="openView(assignment)"
                            >
                              <v-icon dark> mdi-eye </v-icon>
                            </v-btn>
                          </template>
                          <span>View / Record Payment</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              class="ml-2"
                              fab
                              x-small
                              color="primary"
                              :loading="downloadingId === assignment.id"
                              @click="downloadInvoice(assignment)"
                            >
                              <v-icon dark> mdi-file-download-outline </v-icon>
                            </v-btn>
                          </template>
                          <span>Download Invoice</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="9">
                        <AppEmptyState
                          icon="mdi-handshake-outline"
                          title="No stock payments yet"
                          message="Once you assign stock to someone, its loan and installment history will show up here."
                          action-text="Assign Stock"
                          @action="$router.push('/assigned_stocks/create')"
                        />
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination v-model="page" :length="paginationLength">
              </v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- View / Record Payment dialog -->
      <AppDialog
        :show.sync="showViewDialog"
        max-width="900"
        icon="mdi-handshake-outline"
        :title="viewingAssignment ? viewingAssignment.assignment_number : ''"
        subtitle="Stock Assignment Loan"
        :status-text="viewingAssignment ? statusLabels[viewingAssignment.payment_status] : ''"
        :status-color="viewingAssignment ? statusColor(viewingAssignment.payment_status) : 'grey'"
      >
        <StockAssignmentIdPage
          v-if="showViewDialog"
          embedded
          :assignment-id="viewingId"
          @saved="getData"
          @close="showViewDialog = false"
        />
      </AppDialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import AppDialog from "@/components/AppDialog.vue";
import AppEmptyState from "@/components/AppEmptyState.vue";
import AppPageHeader from "@/components/AppPageHeader.vue";
import StockAssignmentIdPage from "./id.vue";

export default {
  name: "stockAssignmentsPage",
  components: { AppDialog, AppEmptyState, AppPageHeader, StockAssignmentIdPage },
  data() {
    return {
      filter: {
        assigned_to_id: "",
        payment_status: "",
      },
      page: 1,
      paginationLength: 0,
      count: 0,
      assignments: [],
      users: [],
      statusLabels: {
        0: "Unpaid",
        1: "Partial",
        2: "Paid",
      },
      statusOptions: [
        { text: "Unpaid", value: 0 },
        { text: "Partial", value: 1 },
        { text: "Paid", value: 2 },
      ],
      isLoading: false,
      downloadingId: null,
      showViewDialog: false,
      viewingId: null,
      viewingAssignment: null,
    };
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
    this.getMasters();
  },
  methods: {
    async getMasters() {
      try {
        const response = await axios.get("/masters", {
          params: { context: "assign_stock" },
        });
        const data = response.data;
        const distributors = (data.distributors || []).map((u) => ({ id: u.id, display_name: u.name }));
        const franchises = (data.franchises || []).map((u) => ({ id: u.id, display_name: u.name }));
        const saleExecutives = (data.sale_executives || []).map((u) => ({ id: u.id, display_name: u.name }));
        this.users = [...distributors, ...franchises, ...saleExecutives];
      } catch (error) {
        console.log(error);
      }
    },
    statusColor(status) {
      const colors = { 0: "error", 1: "warning", 2: "success" };
      return colors[status] || "grey";
    },
    openView(assignment) {
      this.viewingId = this.encryptIt(assignment.id);
      this.viewingAssignment = assignment;
      this.showViewDialog = true;
    },
    async getData() {
      this.isLoading = true;
      this.filter.assigned_to_id = this.filter.assigned_to_id ? this.filter.assigned_to_id : "";
      this.filter.payment_status =
        this.filter.payment_status === 0 || this.filter.payment_status
          ? this.filter.payment_status
          : "";
      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&assigned_to_id=" +
        this.filter.assigned_to_id +
        "&payment_status=" +
        this.filter.payment_status +
        "&search_keyword=" +
        (this.filter.search_keyword || "");
      await axios
        .get(`stock_assignments?${query}`)
        .then((response) => {
          this.assignments = response.data.data;
          this.count = response.data.count;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
        });
      this.isLoading = false;
    },
    async downloadInvoice(assignment) {
      this.downloadingId = assignment.id;
      try {
        const response = await axios.get(
          `/stock_assignments/${this.encryptIt(assignment.id)}/invoice/download`,
          { responseType: "blob" },
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `stock_assignment_${assignment.assignment_number}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
      }
      this.downloadingId = null;
    },
  },
};
</script>
